import React, { useEffect, useState } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

const API = "https://api.sedihisham.com";

// Same keys as the website home page (pages/index.js, components/home/section3)
export const HOME_SECTIONS = [
  { key: "main_slider", label: "Main slider" },
  { key: "shop_by_category", label: "Shop by category" },
  { key: "trust_bar", label: "Trust bar" },
  { key: "week_deals", label: "Deals of the week" },
  {
    key: "products_tabs",
    label: "Products (tabs)",
    tabs: [
      { key: "tab_latest", label: "Latest products" },
      { key: "tab_best_sellers", label: "Best sellers" },
      { key: "tab_special_offers", label: "Special offers" },
    ],
  },
  { key: "second_slider", label: "Second slider" },
  { key: "video_images", label: "Videos & images" },
  { key: "kitchen", label: "Kitchen (cooks)" },
  { key: "news", label: "News" },
  { key: "customer_reviews", label: "Customer reviews" },
  { key: "brands", label: "Our brands" },
];

function VisibilityButton({ label, visible, disabled, onToggle, small }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onToggle}
      className={`flex items-center justify-between gap-3 rounded-lg border px-3 ${
        small ? "py-1 text-[12px]" : "py-2 text-[13px]"
      } font-[400] transition ${
        visible
          ? "border-[#0388CC] bg-[#E9F6FD] text-[#033362]"
          : "border-[#c4c8cf] bg-[#f5f5f5] text-[#9a9a9a] line-through"
      } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      <span>{label}</span>
      <span
        className={`flex items-center gap-1 no-underline ${
          visible ? "text-[#007530]" : "text-[#cc4125]"
        }`}
        style={{ textDecoration: "none" }}
      >
        <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />
        {visible ? "Shown" : "Hidden"}
      </span>
    </button>
  );
}

// Show / hide the sections and product tabs of the website home page.
// Each click is saved right away.
function HomeSectionsVisibility({ disabled }) {
  const [sections, setSections] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/pages/home/sections`)
      .then((response) => {
        setSections(response.data && typeof response.data === "object" ? response.data : {});
      })
      .catch((err) => {
        console.log(err);
        // the server was not updated yet (route missing)
        setUnavailable(true);
      })
      .finally(() => setLoaded(true));
  }, []);

  const isVisible = (key) => sections[key] !== false;

  const toggle = async (key) => {
    const next = !isVisible(key);
    setSavingKey(key);
    try {
      const response = await axios.post(`${API}/pages/home/sections`, {
        sections: { [key]: next },
      });
      setSections(
        response.data && typeof response.data === "object"
          ? response.data
          : { ...sections, [key]: next }
      );
      toast.success(next ? "Shown on the home page" : "Hidden from the home page");
    } catch (err) {
      console.log(err);
      toast.error("Could not save, please try again.");
    }
    setSavingKey(null);
  };

  return (
    <div className="flex mb-10">
      <Toaster position="top-center" />
      <div>
        <div className="text-[#000] font-[700] text-[14px]">0.</div>
      </div>
      <div className="ml-[6%] w-[85%]">
        <p className="text-[#000] font-[700] text-[14px]">
          Home page sections — show / hide
        </p>
        <p className="text-[#707070] text-[12px] font-[400] mb-3">
          Click a section or tab to hide it from the website home page, click
          again to show it. Changes are saved right away.
        </p>
        {!loaded ? (
          <p className="text-[13px] font-[400]">Loading...</p>
        ) : unavailable ? (
          <p className="text-[13px] font-[400] text-[#cc4125]">
            Not available yet — the server needs the latest update.
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {HOME_SECTIONS.map((section) => (
              <div
                key={section.key}
                className={section.tabs ? "col-span-3 rounded-lg border border-dashed border-[#c4c8cf] p-2" : ""}
              >
                <VisibilityButton
                  label={section.label}
                  visible={isVisible(section.key)}
                  disabled={disabled || savingKey !== null}
                  onToggle={() => toggle(section.key)}
                />
                {section.tabs && (
                  <div className="mt-2 ml-4 grid grid-cols-3 gap-2">
                    {section.tabs.map((tab) => (
                      <VisibilityButton
                        key={tab.key}
                        small
                        label={`Tab: ${tab.label}`}
                        visible={isVisible(tab.key)}
                        disabled={disabled || savingKey !== null || !isVisible(section.key)}
                        onToggle={() => toggle(tab.key)}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HomeSectionsVisibility;
